/**
 * #428 (SPEC-425 section 6): Commissions > Import insurer grid.
 *
 * Upload -> choose sheets -> map -> check & preview -> publish. The broker
 * uploads an insurer's workbook exactly as received; it becomes THIS broker's
 * grid (D1). A file with any problem is rejected whole and every problem is
 * listed (D10); labels the file does not define can be mapped right there and
 * the file checked again. Nothing goes live until Publish.
 *
 * #429 (D11): Publish first shows the recalculation of every policy in the
 * grid's period (paid ones included), old vs new commission; confirming applies
 * that reviewed batch, and the batch can be reverted from the last step.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, Input, Modal, ModalFooter, Select, Stepper } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  commissionGridService,
  isRejection,
  type GridDetectResult,
  type GridLayout,
  type GridLayoutConfig,
  type GridPreview,
  type GridPublishResult,
  type GridRecalcBatch,
  type GridRegionInput,
  type GridSheetConfig,
  type GridUploadResult,
} from '@/services/commissionGrids';
import { formatCurrency } from '@/utils/format';
import { useBrokerInsurers } from '@/hooks/useBrokerInsurers';
import { useAuthStore } from '@/store/auth';
import type { BrokerContext } from '@/types';
import {
  BASIS_LABELS,
  FIELD_LABELS,
  RATE_ROLES,
  ROLE_LABELS,
  buildAliases,
  describeRate,
  parseCodes,
  parseColumnList,
  addHeaderRow,
  addKeyColumn,
  parseRowList,
  problemLocation,
  sheetsMissingBasis,
  slabBlocks,
  slabBlocksMissingBasis,
  slabTables,
  splitProblems,
  type SlabBlockConfig,
} from './gridImport';

const STEPS = [
  { id: 'upload', title: 'Upload' },
  { id: 'sheets', title: 'Choose sheets' },
  { id: 'map', title: 'Map' },
  { id: 'preview', title: 'Check & preview' },
  { id: 'publish', title: 'Published' },
];

const dateInputClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100';

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function GridImportWizardPage() {
  const navigate = useNavigate();
  const loginType = useAuthStore((s) => s.loginType);
  const tenantContext = useAuthStore((s) => s.tenantContext);
  const brokerId = loginType === 'broker' ? (tenantContext as BrokerContext | null)?.id ?? undefined : undefined;
  const insurersQuery = useBrokerInsurers(brokerId);

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [insurerId, setInsurerId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [detected, setDetected] = useState<GridDetectResult | null>(null);
  const [config, setConfig] = useState<GridLayoutConfig | null>(null);
  const [layout, setLayout] = useState<GridLayout | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveUntil, setEffectiveUntil] = useState('');
  const [target, setTarget] = useState('new');
  const [gridName, setGridName] = useState('');
  const [basisConfirmed, setBasisConfirmed] = useState(false);
  const [result, setResult] = useState<GridUploadResult | null>(null);
  const [published, setPublished] = useState<GridPublishResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recalc, setRecalc] = useState<GridRecalcBatch | null>(null);
  const [recalcError, setRecalcError] = useState<string | null>(null);
  const [reverted, setReverted] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onDetect = () =>
    run(async () => {
      if (!file || !insurerId) return;
      const body = await commissionGridService.detect(file, insurerId);
      setDetected(body);
      setResult(null);
      setGridName(body.file_name.replace(/\.[^.]+$/, ''));
      if (body.layout) {
        setLayout(body.layout);
        setConfig(body.layout.config);
        setStep(3); // the saved mapping is reused: straight to check & preview
      } else {
        setLayout(null);
        setConfig(body.suggested_config);
        setStep(1);
      }
    });

  const onSaveMapping = () =>
    run(async () => {
      if (!file || !config) return;
      const insurer = insurersQuery.data?.find((i) => i.id === insurerId);
      const saved = await commissionGridService.saveLayout(
        file,
        insurerId,
        `${insurer?.name ?? 'Insurer'} grid layout`,
        config,
        layout?.aliases ?? {},
      );
      setLayout(saved);
      setStep(3);
    });

  const onCheck = (useLayout?: GridLayout) =>
    run(async () => {
      const lay = useLayout ?? layout;
      if (!file || !lay || !effectiveFrom) return;
      const body = await commissionGridService.upload({
        file,
        insurerId,
        layoutId: lay.id,
        effectiveFrom,
        effectiveUntil: effectiveUntil || undefined,
        gridId: target === 'new' ? undefined : target,
        name: target === 'new' ? gridName : undefined,
        basisConfirmed,
      });
      setResult(body);
    });

  // #651: discard a version staged on an earlier attempt, then refresh the list.
  const onDiscardStaged = (versionId: string) =>
    run(async () => {
      await commissionGridService.discard(versionId);
      toast.success('Staged version discarded.');
      if (file && insurerId) {
        const body = await commissionGridService.detect(file, insurerId);
        setDetected((prev) => (prev ? { ...prev, existing_grids: body.existing_grids, staged_versions: body.staged_versions } : body));
        if (target !== 'new' && !body.existing_grids.some((g) => g.id === target)) setTarget('new');
      }
    });

  // #468: a failed preview (409: too large, grid cannot go live, ...) is shown INSIDE the
  // publish dialog with a retry, not only as a toast over a dialog stuck on "Pricing...".
  const loadRecalc = async () => {
    if (!result || isRejection(result)) return;
    setRecalc(null);
    setRecalcError(null);
    setBusy(true);
    try {
      setRecalc(await commissionGridService.recalcPreview(result.version.id));
    } catch (e) {
      setRecalcError(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const openPublish = () => {
    if (!result || isRejection(result)) return;
    setConfirmOpen(true);
    void loadRecalc();
  };

  const onPublish = async () => {
    if (!result || isRejection(result) || !recalc) return;
    setBusy(true);
    try {
      const version = await commissionGridService.publish(result.version.id, recalc.id);
      setPublished(version);
      setReverted(false);
      setConfirmOpen(false);
      setStep(4);
      toast.success('Grid published and its period recalculated.');
    } catch (e) {
      // The batch may be stale or the grid refused: say so in the dialog and let the broker re-run it.
      setRecalc(null);
      setRecalcError(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onRevert = () =>
    run(async () => {
      if (!published?.recalc_batch) return;
      await commissionGridService.revertBatch(published.recalc_batch.id);
      setReverted(true);
      toast.success('Recalculation reverted. The grid is no longer live.');
    });

  const onDiscard = () =>
    run(async () => {
      if (!result || isRejection(result)) return;
      await commissionGridService.discard(result.version.id);
      setResult(null);
      toast.success('Upload discarded. Nothing was published.');
    });

  const updateSheet = (key: string, patch: Partial<GridSheetConfig>) =>
    setConfig((c) => (c ? { sheets: { ...c.sheets, [key]: { ...c.sheets[key], ...patch } } } : c));

  return (
    <div>
      <PageHeader
        title="Import insurer grid"
        subtitle="Load an insurer's commission grid workbook as your grid. Nothing goes live until you publish it."
        breadcrumbs={[{ label: 'Commissions', href: '/commissions' }, { label: 'Import insurer grid' }]}
      />
      <Stepper steps={STEPS} currentStep={step} className="mb-6" />

      {step === 0 && (
        <Card>
          <div className="grid gap-4 md:grid-cols-2">
            <Select
              id="grid-insurer"
              label="Insurer"
              value={insurerId}
              onChange={(e) => setInsurerId(e.target.value)}
              placeholder="Choose the insurer"
              options={(insurersQuery.data ?? []).map((i) => ({ value: i.id, label: i.name }))}
            />
            <div>
              <label htmlFor="grid-file" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Grid workbook (.xlsx)
              </label>
              <input
                id="grid-file"
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-700 dark:text-gray-300"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={onDetect} disabled={!file || !insurerId || busy} isLoading={busy}>
              Read file
            </Button>
          </div>
        </Card>
      )}

      {step === 1 && detected && config && (
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Choose what each sheet holds. Hidden sheets are never read.
          </p>
          <div className="space-y-3">
            {detected.sheets.map((sheet) => (
              <div key={sheet.title} className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {sheet.title}{' '}
                  {sheet.hidden && <Badge variant="default">hidden, skipped</Badge>}
                </div>
                {!sheet.hidden && sheet.key && (
                  <Select
                    aria-label={`Role of ${sheet.title}`}
                    value={config.sheets[sheet.key]?.role ?? 'ignore'}
                    onChange={(e) =>
                      updateSheet(sheet.key as string, {
                        title: sheet.title,
                        role: e.target.value as GridSheetConfig['role'],
                      })
                    }
                    options={detected.roles.map((r) => ({ value: r, label: ROLE_LABELS[r] ?? r }))}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)}>
              Back
            </Button>
            <Button onClick={() => setStep(2)}>Next: map</Button>
          </div>
        </Card>
      )}

      {step === 2 && detected && config && (
        <MappingStep
          detected={detected}
          config={config}
          onChange={updateSheet}
          onBack={() => setStep(1)}
          onSave={onSaveMapping}
          busy={busy}
        />
      )}

      {step === 3 && layout && (
        <div className="space-y-6">
          <Card>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Mapped with <strong>{layout.name}</strong> v{layout.version}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                Edit mapping
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="eff-from" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Effective from (policy start date)
                </label>
                <input id="eff-from" type="date" className={dateInputClass} value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
              </div>
              <div>
                <label htmlFor="eff-until" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Effective until (optional)
                </label>
                <input id="eff-until" type="date" className={dateInputClass} value={effectiveUntil} onChange={(e) => setEffectiveUntil(e.target.value)} />
              </div>
              <Select
                id="grid-target"
                label="Load as"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                options={[
                  { value: 'new', label: 'A new grid (adds a grid for this insurer)' },
                  ...(detected?.existing_grids ?? []).map((g) => ({ value: g.id, label: `New version of ${g.name}` })),
                ]}
              />
              {target === 'new' && <Input id="grid-name" label="Grid name" value={gridName} onChange={(e) => setGridName(e.target.value)} />}
            </div>
            {(detected?.staged_versions?.length ?? 0) > 0 && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/20">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Staged from an earlier attempt, not published
                </p>
                <ul className="mt-2 space-y-1">
                  {detected?.staged_versions?.map((v) => (
                    <li key={v.id} className="flex items-center justify-between gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span className="min-w-0 break-all">
                        {v.grid_name} v{v.version_no} ({v.file_name})
                      </span>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => onDiscardStaged(v.id)}>
                        Discard
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {target === 'new' && (detected?.existing_grids ?? []).some((g) => g.name === gridName.trim()) && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                A grid named &ldquo;{gridName.trim()}&rdquo; already exists for this insurer. This file will be loaded as its next
                version.
              </p>
            )}
            {layout.declared_bases.length > 0 && (
              <label className="mt-4 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={basisConfirmed} onChange={(e) => setBasisConfirmed(e.target.checked)} />
                <span>
                  I confirm the premium basis this file does not state itself:{' '}
                  {layout.declared_bases.map((d) => `${d.sheet}: ${BASIS_LABELS[d.basis] ?? d.basis}`).join('; ')}.
                </span>
              </label>
            )}
            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => onCheck()}
                isLoading={busy}
                disabled={busy || !effectiveFrom || (layout.declared_bases.length > 0 && !basisConfirmed)}
              >
                Check file
              </Button>
            </div>
          </Card>

          {result && isRejection(result) && (
            <RejectionPanel
              result={result}
              insurerId={insurerId}
              layout={layout}
              busy={busy}
              onRetry={(lay) => {
                setLayout(lay);
                onCheck(lay);
              }}
              run={run}
            />
          )}

          {result && !isRejection(result) && (
            <PreviewPanel preview={result} busy={busy} onPublish={openPublish} onDiscard={onDiscard} />
          )}
        </div>
      )}

      {step === 4 && published && (
        <Card>
          {reverted ? (
            <p className="text-gray-900 dark:text-gray-100">
              The recalculation was reverted: every policy is back to its earlier commission and{' '}
              <strong>{published.grid_name}</strong> v{published.version_no} awaits publication again.
            </p>
          ) : (
            <p className="text-gray-900 dark:text-gray-100">
              <strong>{published.grid_name}</strong> v{published.version_no} is live for policies starting from{' '}
              {published.effective_from}.
              {published.recalc_batch
                ? ` ${published.recalc_batch.counts.changed ?? 0} policies recalculated, ${
                    published.recalc_batch.counts.new ?? 0
                  } priced for the first time.`
                : ''}
            </p>
          )}
          <div className="mt-4 flex gap-3">
            <Button onClick={() => navigate('/commissions')}>Back to Commissions</Button>
            {published.recalc_batch && !reverted && (
              <Button variant="outline" onClick={onRevert} isLoading={busy}>
                Revert this recalculation
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setStep(0);
                setResult(null);
                setPublished(null);
                setFile(null);
              }}
            >
              Import another grid
            </Button>
          </div>
        </Card>
      )}

      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} title="Publish this grid?" size="xl">
        {result && !isRejection(result) && (
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {result.row_count} rates from {result.version.file_name} will price policies starting from{' '}
            {result.version.effective_from}
            {result.version.effective_until ? ` to ${result.version.effective_until}` : ''}.
            {result.version.has_file && (
              <Button
                className="ml-2"
                variant="outline"
                size="sm"
                onClick={() =>
                  run(async () =>
                    download(await commissionGridService.downloadOriginal(result.version.id), result.version.file_name),
                  )
                }
              >
                Download original
              </Button>
            )}
          </p>
        )}
        {recalcError ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-100"
          >
            <p>{recalcError}</p>
            <Button className="mt-2" variant="outline" onClick={loadRecalc} isLoading={busy}>
              Try again
            </Button>
          </div>
        ) : recalc ? (
          <RecalcPreview batch={recalc} />
        ) : (
          <p className="mt-3 text-sm text-gray-500">Pricing the period...</p>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={onPublish} isLoading={busy} disabled={!recalc}>
            Publish
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Map step
// ---------------------------------------------------------------------------

function MappingStep({
  detected,
  config,
  onChange,
  onBack,
  onSave,
  busy,
}: {
  detected: GridDetectResult;
  config: GridLayoutConfig;
  onChange: (key: string, patch: Partial<GridSheetConfig>) => void;
  onBack: () => void;
  onSave: () => void;
  busy: boolean;
}) {
  const missing = [...sheetsMissingBasis(config.sheets), ...slabBlocksMissingBasis(config.sheets)];
  const sheets = detected.sheets.filter((s) => !s.hidden && s.key && config.sheets[s.key]?.role !== 'ignore');
  return (
    <div className="space-y-6">
      {sheets.map((sheet) => {
        const key = sheet.key as string;
        const sc = config.sheets[key];
        return (
          <Card key={key}>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{sheet.title}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{ROLE_LABELS[sc.role]}</p>
            <SheetPreview rows={sheet.preview} />
            {RATE_ROLES.includes(sc.role) ? (
              <RateSheetEditor sheetKey={key} sc={sc} detected={detected} onChange={onChange} />
            ) : sc.role === 'incremental' ? (
              <IncrementalSheetEditor sheetKey={key} sc={sc} detected={detected} onChange={onChange} />
            ) : (
              <JsonEditor sheetKey={key} sc={sc} onChange={onChange} />
            )}
          </Card>
        );
      })}
      {missing.length > 0 && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          No premium basis yet for: {missing.join(', ')}. Rows of these sheets (or slab blocks) will be rejected until
          you declare one.
        </p>
      )}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onSave} isLoading={busy} disabled={busy}>
          Save mapping
        </Button>
      </div>
    </div>
  );
}

function SheetPreview({ rows }: { rows: string[][] }) {
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto mb-4 max-h-56 border border-gray-200 dark:border-gray-700 rounded">
      <table className="text-xs text-gray-700 dark:text-gray-300">
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              <td className="px-2 py-1 text-gray-400">{r + 1}</td>
              {row.map((cell, c) => (
                <td key={c} className="px-2 py-1 whitespace-nowrap max-w-[12rem] truncate">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RateSheetEditor({
  sheetKey,
  sc,
  detected,
  onChange,
}: {
  sheetKey: string;
  sc: GridSheetConfig;
  detected: GridDetectResult;
  onChange: (key: string, patch: Partial<GridSheetConfig>) => void;
}) {
  const fieldOptions = detected.fields.map((f) => ({ value: f, label: FIELD_LABELS[f] ?? f }));
  const basis = sc.basis ?? { source: 'declared' as const, value: null };
  const set = (patch: Partial<GridSheetConfig>) => onChange(sheetKey, patch);
  const keyColumns = Object.entries(sc.key_columns ?? {});
  const columnFields = Object.entries(sc.column_fields ?? {});
  const [newKeyCol, setNewKeyCol] = useState('');
  const [newKeyField, setNewKeyField] = useState('');
  const [newHeaderRow, setNewHeaderRow] = useState('');
  const [newHeaderField, setNewHeaderField] = useState('');
  const addOptions = [{ value: '', label: 'Choose a field' }, ...fieldOptions];
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Input id={`${sheetKey}-data-start`} label="First data row" type="number" value={sc.data_start ?? ''} onChange={(e) => set({ data_start: Number(e.target.value) || undefined })} />
      <Input
        id={`${sheetKey}-data-end`}
        label="Last data row (blank = to the end)"
        type="number"
        value={sc.data_end ?? ''}
        onChange={(e) => set({ data_end: e.target.value ? Number(e.target.value) : null })}
      />
      {sc.role === 'rates_matrix' ? (
        <Input
          id={`${sheetKey}-rate-columns`}
          label="Rate columns (e.g. F: or F:BO)"
          value={sc.rate_columns ?? ''}
          onChange={(e) => set({ rate_columns: e.target.value.toUpperCase() })}
        />
      ) : (
        <Input id={`${sheetKey}-rate-column`} label="Rate column" value={sc.rate_column ?? ''} onChange={(e) => set({ rate_column: e.target.value.toUpperCase() })} />
      )}
      {sc.role === 'rates_matrix' && (
        <Input
          id={`${sheetKey}-header-rows`}
          label="Header rows"
          value={(sc.header_rows ?? []).join(', ')}
          onChange={(e) => set({ header_rows: parseRowList(e.target.value) })}
        />
      )}
      <Input
        id={`${sheetKey}-ignore-columns`}
        label="Columns to ignore"
        value={(sc.ignore_columns ?? []).join(', ')}
        onChange={(e) => set({ ignore_columns: parseColumnList(e.target.value) })}
      />
      <Input
        id={`${sheetKey}-ignore-rows`}
        label="Rows to ignore"
        value={(sc.ignore_rows ?? []).join(', ')}
        onChange={(e) => set({ ignore_rows: parseRowList(e.target.value) })}
      />
      <Select
        id={`${sheetKey}-lob`}
        label="Class (all rows)"
        value={sc.lob ?? ''}
        onChange={(e) => set({ lob: e.target.value || null })}
        options={[{ value: '', label: 'Per row (from the segment)' }, ...detected.lobs.map((l) => ({ value: l, label: l }))]}
      />
      {!sc.class_from_mapper && (
        <Input
          id={`${sheetKey}-slab-class`}
          label="Incremental slab class (all rows)"
          value={String((sc.fixed as Record<string, string> | undefined)?.class_group ?? '')}
          placeholder="e.g. PCI, TW"
          onChange={(e) => {
            const fixed = { ...((sc.fixed as Record<string, string> | undefined) ?? {}) };
            if (e.target.value.trim()) fixed.class_group = e.target.value;
            else delete fixed.class_group;
            set({ fixed });
          }}
        />
      )}
      <Select
        id={`${sheetKey}-value-kind`}
        label="Plain numbers are"
        value={sc.value_kind ?? 'points'}
        onChange={(e) => set({ value_kind: e.target.value as 'points' | 'fraction' })}
        options={[
          { value: 'points', label: 'percent points (27 = 27%)' },
          { value: 'fraction', label: 'fractions (0.27 = 27%)' },
        ]}
      />
      <Select
        id={`${sheetKey}-basis`}
        label="Premium basis"
        value={basis.source === 'sheet_text' ? `sheet:${basis.cell ?? ''}` : basis.value ?? ''}
        onChange={(e) =>
          set({
            basis: e.target.value.startsWith('sheet:')
              ? { source: 'sheet_text', cell: e.target.value.slice(6) }
              : { source: 'declared', value: e.target.value || null },
          })
        }
        options={[
          ...(basis.source === 'sheet_text' ? [{ value: `sheet:${basis.cell ?? ''}`, label: `As the sheet states (${basis.cell})` }] : []),
          { value: '', label: 'Not declared' },
          ...detected.premium_bases.map((b) => ({ value: b, label: `Declare: ${BASIS_LABELS[b] ?? b}` })),
        ]}
      />
      <div className="md:col-span-3">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Key columns</p>
        <div className="grid gap-2 md:grid-cols-3">
          {keyColumns.map(([col, field]) => (
            <Select
              key={col}
              id={`${sheetKey}-col-${col}`}
              label={`Column ${col}`}
              value={field}
              onChange={(e) => set({ key_columns: { ...(sc.key_columns ?? {}), [col]: e.target.value } })}
              options={fieldOptions}
            />
          ))}
        </div>
        <div className="grid gap-2 md:grid-cols-3 mt-2 items-end">
          <Input id={`${sheetKey}-new-key-col`} label="Add key column (letter)" value={newKeyCol} onChange={(e) => setNewKeyCol(e.target.value)} />
          <Select id={`${sheetKey}-new-key-field`} label="Holds" value={newKeyField} onChange={(e) => setNewKeyField(e.target.value)} options={addOptions} />
          <Button
            variant="outline"
            disabled={!addKeyColumn(sc, newKeyCol, newKeyField)}
            onClick={() => {
              const patch = addKeyColumn(sc, newKeyCol, newKeyField);
              if (!patch) return;
              set(patch);
              setNewKeyCol('');
              setNewKeyField('');
            }}
          >
            Add key column
          </Button>
        </div>
      </div>
      {sc.role === 'rates_matrix' && (
        <div className="md:col-span-3">
          <div className="grid gap-2 md:grid-cols-3 items-end">
            <Input id={`${sheetKey}-new-header-row`} label="Add header row (number)" value={newHeaderRow} onChange={(e) => setNewHeaderRow(e.target.value)} />
            <Select id={`${sheetKey}-new-header-field`} label="Holds" value={newHeaderField} onChange={(e) => setNewHeaderField(e.target.value)} options={addOptions} />
            <Button
              variant="outline"
              disabled={!addHeaderRow(sc, newHeaderRow, newHeaderField)}
              onClick={() => {
                const patch = addHeaderRow(sc, newHeaderRow, newHeaderField);
                if (!patch) return;
                set(patch);
                setNewHeaderRow('');
                setNewHeaderField('');
              }}
            >
              Add header row
            </Button>
          </div>
        </div>
      )}
      {columnFields.length > 0 && (
        <div className="md:col-span-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Header rows over the rates</p>
          <div className="grid gap-2 md:grid-cols-3">
            {columnFields.map(([row, field]) => (
              <Select
                key={row}
                id={`${sheetKey}-row-${row}`}
                label={`Row ${row}`}
                value={field}
                onChange={(e) => set({ column_fields: { ...(sc.column_fields ?? {}), [row]: e.target.value } })}
                options={fieldOptions}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * #430: an incremental rule sheet, block by block. The class each block prices
 * and, above all, the premium its monthly volume is counted on: a block whose
 * header states no basis ("Slabs") must have one declared, or every slab row of
 * it is rejected (D6: the slab basis is whatever the rule sheet states).
 */
function IncrementalSheetEditor({
  sheetKey,
  sc,
  detected,
  onChange,
}: {
  sheetKey: string;
  sc: GridSheetConfig;
  detected: GridDetectResult;
  onChange: (key: string, patch: Partial<GridSheetConfig>) => void;
}) {
  const blocks = slabBlocks(sc);
  const setBlock = (i: number, patch: Partial<SlabBlockConfig>) =>
    onChange(sheetKey, { blocks: blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  if (!blocks.length) {
    return <p className="text-sm text-amber-700 dark:text-amber-400">No slab block was found on this sheet.</p>;
  }
  return (
    <div className="space-y-4">
      {blocks.map((block, i) => {
        const vb = block.volume_basis ?? { source: 'declared' as const, value: null };
        return (
          <div key={i} className="grid gap-4 md:grid-cols-3" data-testid={`slab-block-${i}`}>
            {block.class_column ? (
              <Input
                id={`${sheetKey}-block-${i}-class-column`}
                label="Class column"
                value={block.class_column}
                onChange={(e) => setBlock(i, { class_column: e.target.value.toUpperCase() })}
              />
            ) : (
              <Input
                id={`${sheetKey}-block-${i}-class`}
                label="Class (whole block)"
                value={block.class ?? ''}
                onChange={(e) => setBlock(i, { class: e.target.value })}
              />
            )}
            <Input
              id={`${sheetKey}-block-${i}-slab-column`}
              label="Slab column"
              value={block.slab_column}
              onChange={(e) => setBlock(i, { slab_column: e.target.value.toUpperCase() })}
            />
            <Input
              id={`${sheetKey}-block-${i}-increment-column`}
              label="Increment column (% points)"
              value={block.increment_column}
              onChange={(e) => setBlock(i, { increment_column: e.target.value.toUpperCase() })}
            />
            <Select
              id={`${sheetKey}-block-${i}-volume-basis`}
              label="Monthly volume counted on"
              value={vb.source === 'sheet_text' ? `sheet:${vb.cell ?? ''}` : vb.value ?? ''}
              onChange={(e) =>
                setBlock(i, {
                  volume_basis: e.target.value.startsWith('sheet:')
                    ? { source: 'sheet_text', cell: e.target.value.slice(6) }
                    : { source: 'declared', value: e.target.value || null },
                })
              }
              options={[
                ...(vb.source === 'sheet_text' ? [{ value: `sheet:${vb.cell ?? ''}`, label: `As the sheet states (${vb.cell})` }] : []),
                { value: '', label: 'Not declared' },
                ...detected.premium_bases.map((b) => ({ value: b, label: `Declare: ${BASIS_LABELS[b] ?? b}` })),
              ]}
            />
          </div>
        );
      })}
    </div>
  );
}

function JsonEditor({
  sheetKey,
  sc,
  onChange,
}: {
  sheetKey: string;
  sc: GridSheetConfig;
  onChange: (key: string, patch: Partial<GridSheetConfig>) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(sc, null, 2));
  const [error, setError] = useState('');
  return (
    <div>
      <label htmlFor={`json-${sheetKey}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Sheet mapping
      </label>
      <textarea
        id={`json-${sheetKey}`}
        className="w-full font-mono text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-gray-900 dark:text-gray-100"
        rows={8}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(sheetKey, JSON.parse(e.target.value));
            setError('');
          } catch {
            setError('Not valid JSON yet.');
          }
        }}
      />
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rejected file: every problem, and the labels that can be mapped here
// ---------------------------------------------------------------------------

const REGION_KINDS: { value: GridRegionInput['kind']; label: string }[] = [
  { value: 'rto_set', label: 'RTO set' },
  { value: 'city', label: 'City (RTO set)' },
  { value: 'zone', label: 'Zone (RTO set)' },
  { value: 'metro_set', label: 'Metro RTO set' },
  { value: 'state', label: 'State' },
  { value: 'multi_state', label: 'Several states' },
];

function RejectionPanel({
  result,
  insurerId,
  layout,
  busy,
  onRetry,
  run,
}: {
  result: Extract<GridUploadResult, { status: 'rejected' }>;
  insurerId: string;
  layout: GridLayout;
  busy: boolean;
  onRetry: (layout: GridLayout) => void;
  run: (fn: () => Promise<void>) => Promise<void>;
}) {
  const { labels, others } = useMemo(() => splitProblems(result.problems), [result.problems]);
  const [entries, setEntries] = useState<Record<string, { text: string; kind: GridRegionInput['kind'] }>>({});
  const entryKey = (field: string, label: string) => `${field}|${label}`;

  const onSaveLabels = () =>
    run(async () => {
      const aliasEntries = labels.map((l) => ({
        field: l.field,
        label: l.label,
        text: entries[entryKey(l.field, l.label)]?.text ?? '',
      }));
      const aliases = buildAliases(aliasEntries);
      if (typeof aliases === 'string') throw new Error(aliases);
      for (const l of labels.filter((x) => x.field === 'geo')) {
        const entry = entries[entryKey(l.field, l.label)];
        if (!entry?.text.trim()) continue;
        const kind = entry.kind ?? 'rto_set';
        const values = parseCodes(entry.text);
        await commissionGridService.saveRegion({
          insurerId,
          label: l.label,
          kind,
          rtoCodes: kind === 'state' || kind === 'multi_state' ? [] : values,
          states: kind === 'state' || kind === 'multi_state' ? values : [],
        });
      }
      const next = Object.keys(aliases).length ? await commissionGridService.addAliases(layout.id, aliases) : layout;
      onRetry(next);
    });

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="font-semibold text-red-700 dark:text-red-400">
          File rejected: {result.problems.length} problem{result.problems.length === 1 ? '' : 's'}. Nothing was loaded.
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            run(async () =>
              download(await commissionGridService.problemsReport(result.import_run.id), `${result.import_run.file_name}-problems.csv`),
            )
          }
        >
          Download problems report
        </Button>
      </div>

      {labels.length > 0 && (
        <div className="mb-6">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Labels this file uses that are not mapped yet. Say once what each means; it is saved for this insurer.
            Geography: RTO codes (e.g. MH-01, MH02) or state names. Other labels: field=value pairs, e.g.
            lob=pcv; seating_from=1; seating_to=6 (several meanings separated by |; any = narrows nothing).
          </p>
          <div className="space-y-2">
            {labels.map((l) => {
              const k = entryKey(l.field, l.label);
              const entry = entries[k] ?? { text: '', kind: 'rto_set' as const };
              return (
                <div key={k} className="grid gap-2 md:grid-cols-4 items-end">
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    <strong>{l.label}</strong>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {FIELD_LABELS[l.field] ?? l.field} - blocks {l.count} cell{l.count === 1 ? '' : 's'}
                    </span>
                  </div>
                  {l.field === 'geo' ? (
                    <Select
                      aria-label={`Kind for ${l.label}`}
                      value={entry.kind}
                      onChange={(e) => setEntries({ ...entries, [k]: { ...entry, kind: e.target.value as GridRegionInput['kind'] } })}
                      options={REGION_KINDS}
                    />
                  ) : (
                    <div />
                  )}
                  <div className="md:col-span-2">
                    <Input
                      aria-label={`Meaning of ${l.label}`}
                      value={entry.text}
                      onChange={(e) => setEntries({ ...entries, [k]: { ...entry, text: e.target.value } })}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={onSaveLabels} isLoading={busy} disabled={busy}>
              Save meanings and check again
            </Button>
          </div>
        </div>
      )}

      {others.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-4">Where</th>
                <th className="py-1 pr-4">Value</th>
                <th className="py-1 pr-4">Problem</th>
              </tr>
            </thead>
            <tbody>
              {others.map((p, i) => (
                <tr key={i} className="border-t border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-200">
                  <td className="py-1 pr-4 whitespace-nowrap">{problemLocation(p)}</td>
                  <td className="py-1 pr-4">{p.value}</td>
                  <td className="py-1 pr-4">
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{p.code}</span> {p.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Clean file: the preview the broker confirms
// ---------------------------------------------------------------------------

function PreviewPanel({
  preview,
  busy,
  onPublish,
  onDiscard,
}: {
  preview: GridPreview;
  busy: boolean;
  onPublish: () => void;
  onDiscard: () => void;
}) {
  const live = preview.version.status === 'active';
  return (
    <div className="space-y-6">
      <Card>
        {preview.duplicate && (
          <p className="mb-3 text-sm text-blue-700 dark:text-blue-300">
            This exact file was already loaded{live ? ' and is live' : ''}. Nothing new was created.
          </p>
        )}
        <div className="flex flex-wrap gap-6 text-sm text-gray-800 dark:text-gray-200">
          <div>
            <div className="text-gray-500 dark:text-gray-400">Rates to load</div>
            <div className="text-2xl font-semibold">{preview.row_count}</div>
          </div>
          {Object.entries(preview.by_class).map(([lob, info]) => (
            <div key={lob}>
              <div className="text-gray-500 dark:text-gray-400">{lob}</div>
              <div className="font-semibold">{info.rows} rates</div>
            </div>
          ))}
          {Object.entries(preview.declines).map(([kind, info]) => (
            <div key={kind}>
              <div className="text-gray-500 dark:text-gray-400">{kind}</div>
              <div className="font-semibold">
                {info.count} rows, {info.rto_count} RTOs
              </div>
            </div>
          ))}
          <div>
            <div className="text-gray-500 dark:text-gray-400">Slabs</div>
            <div className="font-semibold">{preview.slabs.length}</div>
          </div>
        </div>
        <table className="mt-4 min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 dark:text-gray-400">
              <th className="py-1 pr-4">Sheet</th>
              <th className="py-1 pr-4">Read as</th>
              <th className="py-1 pr-4">Rows</th>
              <th className="py-1 pr-4">Left out</th>
            </tr>
          </thead>
          <tbody>
            {preview.sheets.map((s) => (
              <tr key={s.title} className="border-t border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-200">
                <td className="py-1 pr-4">{s.title}</td>
                <td className="py-1 pr-4">{ROLE_LABELS[s.role] ?? s.role}</td>
                <td className="py-1 pr-4">{s.rows}</td>
                <td className="py-1 pr-4 text-xs">
                  {(s.ignored_rows ?? []).map((r) => `row ${r.row} (${r.reason})`).join('; ')}
                  {(s.ignored_columns ?? []).length ? ` columns ${(s.ignored_columns ?? []).join(', ')}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {preview.slabs.length > 0 && (
        <Card>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Incremental slabs (monthly premium)</h3>
          <div className="grid gap-4 md:grid-cols-2" data-testid="slab-tables">
            {slabTables(preview.slabs).map((table) => (
              <div key={table.classGroup}>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {table.classGroup}{' '}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    volume on {BASIS_LABELS[table.volumeBasis] ?? table.volumeBasis}
                  </span>
                </p>
                <table className="min-w-full text-sm">
                  <tbody>
                    {table.slabs.map((slab) => (
                      <tr key={slab.sourceRef} className="border-t border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-200">
                        <td className="py-1 pr-4">{slab.range}</td>
                        <td className="py-1 pr-4">+{slab.points} pts</td>
                        <td className="py-1 pr-4 text-xs text-gray-500 dark:text-gray-400">{slab.sourceRef}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Sample of the final rates</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-4">Cell</th>
                <th className="py-1 pr-4">Class</th>
                <th className="py-1 pr-4">Segment</th>
                <th className="py-1 pr-4">Policy type</th>
                <th className="py-1 pr-4">Geography</th>
                <th className="py-1 pr-4">Rate</th>
              </tr>
            </thead>
            <tbody>
              {preview.sample_rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-200">
                  <td className="py-1 pr-4 whitespace-nowrap">{r.source_ref}</td>
                  <td className="py-1 pr-4">
                    {r.lob}
                    {r.class_group ? ` / ${r.class_group}` : ''}
                  </td>
                  <td className="py-1 pr-4">{r.insurer_segment}</td>
                  <td className="py-1 pr-4">{r.policy_type || 'any'}</td>
                  <td className="py-1 pr-4">
                    {r.geo_label} <span className="text-xs text-gray-500">({r.geo_level})</span>
                  </td>
                  <td className="py-1 pr-4">{describeRate(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {(preview.conditions.length > 0 || preview.warnings.length > 0) && (
        <Card>
          {preview.conditions.length > 0 && (
            <>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Written conditions</h3>
              <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200 mb-4">
                {preview.conditions.map((c) => (
                  <li key={c.id}>
                    <Badge variant={c.enforced ? 'success' : 'default'}>{c.enforced ? 'Enforced' : 'Shown'}</Badge>{' '}
                    <span className="text-xs text-gray-500 dark:text-gray-400">{c.source_ref}</span> {c.text}
                  </li>
                ))}
              </ul>
            </>
          )}
          {preview.warnings.length > 0 && (
            <>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Warnings (not blocking)</h3>
              <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                {preview.warnings.map((w, i) => (
                  <li key={i}>
                    {problemLocation(w)}: {w.message}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      )}

      {!live && (
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onDiscard} disabled={busy}>
            Discard
          </Button>
          <Button onClick={onPublish} disabled={busy}>
            Publish grid
          </Button>
        </div>
      )}
      {live && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <Link to="/commissions" className="text-primary-600 dark:text-primary-400">
            Back to Commissions
          </Link>
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// #429: the recalculation preview shown before publishing (D11)
// ---------------------------------------------------------------------------

const OUTCOME_LABELS: Record<string, string> = {
  changed: 'Recalculated',
  unchanged: 'Unchanged',
  new: 'First calculation',
  manual_rate_kept: 'Hand-entered rate kept',
  no_rate: 'No grid rate (left as is)',
  skipped: 'Refused by the grid (left as is)',
  skipped_cancelled: 'Cancelled (left as is)',
  error: 'Could not be priced',
};

function money(value: string | undefined) {
  return value === undefined ? '-' : formatCurrency(value);
}

export function RecalcPreview({ batch }: { batch: GridRecalcBatch }) {
  const items = batch.items ?? [];
  const all = batch.totals.all ?? {};
  const paid = batch.totals.paid ?? {};
  const loss = batch.coverage_loss ?? [];
  return (
    <div className="mt-4 space-y-3 text-sm text-gray-800 dark:text-gray-200">
      {loss.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
        >
          <p className="font-semibold">Publishing this version leaves part of the earlier grid uncovered.</p>
          <ul className="mt-1 list-disc pl-5">
            {loss.map((item) => (
              <li key={`${item.kind}-${item.previous_version}-${item.from}`}>
                {item.classes.join(', ').toUpperCase()}:{' '}
                {item.kind === 'dates'
                  ? `no grid rate for policies starting ${item.from}${item.to ? ` to ${item.to}` : ' onwards'}`
                  : `no longer priced by this grid from ${item.from}${item.to ? ` to ${item.to}` : ''}`}{' '}
                (was priced by {item.previous_version}). Those policies fall back to the other commission rates.
              </li>
            ))}
          </ul>
        </div>
      )}
      <p>
        {items.length === 0
          ? 'No policy starts in this period, so nothing is recalculated.'
          : `${batch.counts.policies ?? items.length} policies start in this period (${batch.counts.paid ?? 0} already paid). ` +
            `${batch.counts.changed ?? 0} will be recalculated and ${batch.counts.new ?? 0} priced for the first time. ` +
            'Money already paid moves only through adjustment entries; nothing posted is edited.'}
      </p>
      {items.length > 0 && (
        <>
          <div className="flex flex-wrap gap-6">
            {(['total', 'broker', 'posp'] as const).map((party) => (
              <div key={party}>
                <div className="text-gray-500 dark:text-gray-400">
                  {party === 'total' ? 'Received from insurer' : party === 'broker' ? 'Broker' : 'POSP'}
                </div>
                <div className="font-semibold">
                  {money(all[party]?.before)} to {money(all[party]?.after)} ({money(all[party]?.delta)})
                </div>
                {paid[party] && <div className="text-xs text-gray-500">paid policies: {money(paid[party].delta)}</div>}
              </div>
            ))}
          </div>
          <div className="max-h-80 overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-1 pr-4">Policy</th>
                  <th className="py-1 pr-4">Starts</th>
                  <th className="py-1 pr-4">Outcome</th>
                  <th className="py-1 pr-4">Old commission</th>
                  <th className="py-1 pr-4">New commission</th>
                  <th className="py-1 pr-4">Difference</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-t border-gray-100 dark:border-gray-800" title={item.message || item.rate_source}>
                    <td className="py-1 pr-4">
                      {item.policy_number}
                      {item.was_paid && (
                        <Badge variant="warning" className="ml-2">
                          Paid
                        </Badge>
                      )}
                    </td>
                    <td className="py-1 pr-4">{item.policy_start}</td>
                    <td className="py-1 pr-4">{OUTCOME_LABELS[item.outcome] ?? item.outcome}</td>
                    <td className="py-1 pr-4">{money(item.before.total)}</td>
                    <td className="py-1 pr-4">{money(item.after.total)}</td>
                    <td className="py-1 pr-4">{money(item.delta.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
